import { useMemo } from 'react';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import {
  FF_MODEL_PRICE_PROMOTIONS,
  FF_MODEL_PRICE_PROMOTIONS_PAID,
  FF_MODEL_PRICE_PROMOTIONS_MAX_TIER,
} from '@/consts/featureFlags';
import { parseActivePromotions, type ModelPromotion } from '@/utils/modelPromotions';

/**
 * Active model-price promotions for the current user, merged across every audience flag — the same
 * set the server reads in `PROMOTION_FLAGS`. If the client read fewer flags than the server, a user
 * would be charged a promo price while the node still quoted the full one. Each flag is read
 * explicitly because hooks cannot be called in a loop.
 */
export const useActivePromotions = (): ModelPromotion[] => {
  const allUsersEnabled = useFeatureFlagEnabled(FF_MODEL_PRICE_PROMOTIONS);
  const allUsersPayload = useFeatureFlagPayload(FF_MODEL_PRICE_PROMOTIONS);
  const paidEnabled = useFeatureFlagEnabled(FF_MODEL_PRICE_PROMOTIONS_PAID);
  const paidPayload = useFeatureFlagPayload(FF_MODEL_PRICE_PROMOTIONS_PAID);
  const maxTierEnabled = useFeatureFlagEnabled(FF_MODEL_PRICE_PROMOTIONS_MAX_TIER);
  const maxTierPayload = useFeatureFlagPayload(FF_MODEL_PRICE_PROMOTIONS_MAX_TIER);
  return useMemo(
    () => [
      ...(allUsersEnabled === true ? parseActivePromotions(allUsersPayload) : []),
      ...(paidEnabled === true ? parseActivePromotions(paidPayload) : []),
      ...(maxTierEnabled === true ? parseActivePromotions(maxTierPayload) : []),
    ],
    [allUsersEnabled, paidEnabled, maxTierEnabled, allUsersPayload, paidPayload, maxTierPayload],
  );
};
